import {ChangeDetectionStrategy, Component, computed, inject, input, OnInit, signal} from '@angular/core';
import {UserService} from "../../services/user.service";
import {FormsModule} from "@angular/forms";
import {UserOverviewDto} from "admin-api-dto";
import {Select, SelectChangeEvent} from "primeng/select";
import {DatePipe} from "@angular/common";
import {PrimeTemplate} from "primeng/api";
import {TableModule} from "primeng/table";
import {Tag} from "primeng/tag";
import {ActivatedRoute, Router} from "@angular/router";
import {TypedTableRowDirective} from "../../typed-table-row.directive";
import {NewWindowLinkComponent} from "../../components/new-window-link/new-window-link.component";
import {InputText} from "primeng/inputtext";
import Fuse, {IFuseOptions} from 'fuse.js'
import {Button} from "primeng/button";
import {firstValueFrom, timeout} from "rxjs";

const searchKeys: (keyof UserOverviewDto)[] = [
  'id',
  'email',
  'firstName',
  'lastName',
  'nycMail1',
  'nycMail2',
  'nycUsername',
]

const fuseOptions: IFuseOptions<UserOverviewDto> = {
  minMatchCharLength: 3,
  threshold: 0.2,
  keys: searchKeys
};

@Component({
  selector: 'app-better-users-overview',
  imports: [
    FormsModule,
    Select,
    DatePipe,
    PrimeTemplate,
    TableModule,
    Tag,
    TypedTableRowDirective,
    NewWindowLinkComponent,
    InputText,
    Button
  ],
  templateUrl: './better-users-overview.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BetterUsersOverviewComponent implements OnInit {
  private static readonly REQUEST_TIMEOUT_MS = 5000;
  private readonly allUsersAfterSignupFlow = signal<UserOverviewDto[]>([]);
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  protected readonly selectedUsers = signal<UserOverviewDto[]>([]);
  protected readonly bulkReconnectLoading = signal(false);
  protected readonly bulkReconnectMessage = signal<string | undefined>(undefined);

  protected readonly view = input<'to-register' | 'all' | 'to-sync' | 'active' | 'mandatory-flow' | 'broken'>('to-register');
  protected readonly finalView = computed(() => {
    const view = this.view();

    return view ?? 'to-register'; // Default value if the provided value is not valid
  });

  downloadCsv() {
    this.userService.requestCsvExport().subscribe({
      next: () => window.alert('Export requested. Check your email for the download link.'),
      error: () => window.alert('Failed to request export. Please try again.')
    });
  }

  copyToApply() {
    const users = this.selectedUsers().map(
      user => ({
        username: user.nycUsername,
        password: user.nycPassword,
        firstName: user.firstName,
        lastName: user.lastName,
        email1: user.nycMail1,
        email2: user.nycMail2,
      })
    )

    document.dispatchEvent(new CustomEvent("registerUsers", {detail: users}));
  }

  protected readonly search = input('');

  protected onModeChange(event: SelectChangeEvent) {
    debugger
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {view: event.value},
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }
  protected onSearchChange(value: string) {
    debugger
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {search: value},
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  public ngOnInit() {
    this.userService.getUsersAfterSignupFlow().subscribe(
      users => this.allUsersAfterSignupFlow.set(users)
    )
  }


  protected readonly users = computed(
    () => this.allUsersAfterSignupFlow().filter(user => {
      switch (this.finalView()) {
        case 'to-register':
          return user.status === 'active' && user.nycProfileStatus !== 'complete';
        case 'all':
          return true;
        case 'to-sync':
          return user.status === 'active' && user.nycProfileStatus === 'complete' && user.nycSyncStatus !== 'synced';
        case 'active':
          return user.status === 'active';
        case 'mandatory-flow':
          return user.status === 'mandatory-flow';
        case 'broken':
          return user.isNycBroken;
      }
    })
  )

  protected readonly searchedUsers = computed(() => {
    const searchValue = this.search() ?? '';
    if (searchValue.trim().length === 0) {
      return this.users();
    }
    const fuse = new Fuse(this.users(), fuseOptions);
    const results = fuse.search(searchValue);
    return results.map(result => result.item);
  });

  protected readonly sortedUsers = computed(() => {
    return [...this.searchedUsers()].sort((a, b) => {
      if (a.ignore && !b.ignore) return 1;
      if (!a.ignore && b.ignore) return -1;
      if (a.createdAt > b.createdAt) return -1;
      if (a.createdAt < b.createdAt) return 1;
      return 0;
    });
  });

  protected readonlyViewOptions = [
    {
      label: 'To register',
      value: 'to-register'
    },
    {
      label: 'All',
      value: 'all'
    },
    {
      label: 'To be synced soon',
      value: 'to-sync'
    },
    {
      label: 'Active',
      value: 'active'
    },
    {
      label: 'Mandatory Flow',
      value: 'mandatory-flow'
    },
    {
      label: 'Broken',
      value: 'broken'
    },
  ] as const;

  openById(){
    const confirmedId = prompt('Enter the user ID to open:');

    if (confirmedId) {
      this.router.navigate(['/users', confirmedId]);
    }
  }

  protected viewOptions = [...this.readonlyViewOptions]

  protected readonly onchange = onchange;

  protected async reconnectFirstTwentyHouseholds() {
    if (this.bulkReconnectLoading()) {
      return;
    }

    this.bulkReconnectLoading.set(true);
    this.bulkReconnectMessage.set(undefined);

    try {
      const errors: string[] = [];

      const fetchWithTimeout = async <T>(observable: any, onErrorId: string): Promise<T | undefined> => {
        try {
          return await firstValueFrom(
            observable.pipe(timeout(BetterUsersOverviewComponent.REQUEST_TIMEOUT_MS))
          );
        } catch (error) {
          errors.push(onErrorId);
          return undefined;
        }
      };

      const activeUsers = this.allUsersAfterSignupFlow()

      if (!activeUsers) {
        this.bulkReconnectMessage.set('Failed to load users for reconnection (possibly timed out).');
        return;
      }

      const candidates = activeUsers.filter(
        user => !user.ignore && user.nycSyncStatus === 'sync-failed'
      );

      let processed = 0;
      let reconnected = 0;

      for (const candidate of candidates) {
        if (processed >= 20) {
          break;
        }

        const detail = await firstValueFrom(
          this.userService.getUser(candidate.id),
        );
        if (!detail) {
          continue;
        }

        const householdId = detail.householdInfo?.nycData.nycId;
        const syncStatus = detail.householdInfo?.nycData.syncStatus;

        if (detail.ignore) {
          continue;
        }
        if (householdId !== undefined && householdId !== null) {
          continue;
        }
        if (syncStatus !== 'sync-failed') {
          continue;
        }

        processed++;

        const reconnectResult = await fetchWithTimeout(
          this.userService.reconnectHousehold(detail.id),
          detail.id
        );
        if (reconnectResult !== undefined) {
          reconnected++;
        }
      }

      this.bulkReconnectMessage.set(
        `Processed ${processed} users, reconnected ${reconnected}${errors.length ? `. Errors: ${errors.join(', ')}` : ''}`
      );
    } finally {
      this.bulkReconnectLoading.set(false);
    }
  }
}
